import React from "react"
import { ChevronDown, Info } from "lucide-react"
import { useColorModeValue } from "@chakra-ui/react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"

import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
} from "./brokerAlertUi"

/** @see src/Branding/brand-guidelines.md — Primary Gradient for info panels */
export const BRAND_INFO_GRADIENT = "linear-gradient(135deg, #38BBD1, #3EEAEC)"

/** @see src/Branding/brand-guidelines.md — Glacial Orange for warning panels */
export const BRAND_WARNING_GRADIENT = "linear-gradient(135deg, #F45B0B, #F49113)"

export const BRAND_GUIDANCE = {
  tropicalSurf: "#38BBD1",
  skyGlacier: "#73E6F0",
  mintBreeze: "#96FBF7",
} as const

/** Match IBKR guidance body: gray-500 light / slate-400 dark (not pure white). */
export function useBrokerGuidanceColors() {
  const bodyColor = useColorModeValue("#6B7280", "#94A3B8")
  return {
    bodyColor,
    headingColor: BRAND_GUIDANCE.tropicalSurf,
    labelColor: bodyColor,
    iconColor: BRAND_GUIDANCE.tropicalSurf,
    noteColor: bodyColor,
    pillBg: "color-mix(in oklab, var(--brand-tropical-surf) 10%, transparent)",
    pillColor: "#2C7A8B",
    pillBorder: "color-mix(in oklab, var(--brand-tropical-surf) 45%, transparent)",
  }
}

export interface BrokerGuidancePanelProps {
  title: string
  compact?: boolean
  mb?: number
  /**
   * When true, renders as a disclosure accordion (header toggles the body).
   * Defaults to false (always expanded alert).
   */
  collapsible?: boolean
  /** Initial open state when `collapsible` is true. Defaults to false (collapsed). */
  defaultOpen?: boolean
  /**
   * Optional lead paragraph rendered at the top of the expanded body.
   * Prefer putting intro copy here or as the first child — not both.
   */
  summary?: React.ReactNode
  children: React.ReactNode
}

const PANEL_SHELL_CLASS =
  "rounded-[6px] border border-[color-mix(in_oklab,var(--brand-tropical-surf)_45%,transparent)] bg-[color-mix(in_oklab,var(--brand-tropical-surf)_10%,transparent)] font-[family-name:var(--font-primary)]"

const BrokerGuidancePanel = ({
  title,
  compact = false,
  mb = 4,
  collapsible = false,
  defaultOpen = false,
  summary,
  children,
}: BrokerGuidancePanelProps): React.ReactElement => {
  const [open, setOpen] = React.useState(defaultOpen)

  if (!collapsible) {
    return (
      <Alert
        variant="info"
        className={cn(
          BROKER_ALERT_CLASS,
          compact ? "py-2.5" : undefined,
          mb > 0 ? "mb-4" : undefined
        )}
      >
        <BrokerAlertIcon tone="info" />
        <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>{title}</AlertTitle>
        <AlertDescription
          className={cn(
            BROKER_ALERT_DESC_CLASS,
            "col-start-2 space-y-3 [&_p]:mb-0 [&_p:not(:last-child)]:mb-0"
          )}
        >
          {summary}
          {children}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      data-broker-guidance-collapsible=""
      className={cn(PANEL_SHELL_CLASS, compact ? "px-3 py-3" : "px-3.5 py-3.5", mb > 0 && "mb-4")}
    >
      <button
        type="button"
        data-broker-guidance-toggle=""
        aria-expanded={open}
        className={cn(
          "flex w-full min-w-0 items-center gap-2.5 text-left",
          "rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-tropical-surf)]/35"
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <Info
          aria-hidden
          className="size-4 shrink-0 text-[color:var(--brand-tropical-surf)]"
        />
        <span
          className={cn(
            BROKER_ALERT_TITLE_CLASS,
            "min-w-0 flex-1 font-bold leading-snug"
          )}
        >
          {title}
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            "size-4 shrink-0 text-[color:var(--brand-tropical-surf)] transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      <CollapsibleContent className="min-w-0 w-full overflow-hidden">
        <div
          className={cn(
            BROKER_ALERT_DESC_CLASS,
            "mt-3 min-w-0 w-full border-t border-[color-mix(in_oklab,var(--brand-tropical-surf)_25%,transparent)] pt-3 pb-1 text-left"
          )}
        >
          {summary}
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export default BrokerGuidancePanel
